/**
 * Normalisierung Verkauf → Session / Écart / Kasse
 */

import { resolveSaleUnpaidAmount } from '../domain/customers/balance/saleBalance';
import { getSaleTotalAmount } from '../domain/restaurant/restaurantDishSaleService';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout/paymentAllocation';

export function normalizeSaleTotal(sale) {
  if (!sale || typeof sale !== 'object') return 0;
  const n = Number(getSaleTotalAmount(sale));
  return Number.isFinite(n) ? n : 0;
}

/** Für Écart: Créance wenn Domain unpaid > 0 (SSOT: resolveSaleUnpaidAmount). */
export function isCreditSale(sale) {
  if (!sale) return false;
  return resolveSaleUnpaidAmount(sale) > 0;
}

export function normalizePaymentMethodForSession(sale) {
  if (!sale) return 'cash';
  const pm = String(sale.paymentMethod || sale.paymentType || 'cash').toLowerCase();
  const provider = String(sale.electronicPaymentMethod || '').toLowerCase();

  // Créance (full credit) — only when method is credit
  if (pm === 'credit' || pm === 'credit_payment' || pm === 'on_account') {
    if (isCreditSale(sale)) return 'credit';
  }

  // Cash wins over a stale electronic provider left in UI/draft state
  if (pm === 'cash' || pm === 'espèces' || pm === 'especes') {
    return 'cash';
  }

  if (pm === 'electronic' || pm === 'électronique' || pm === 'card' || pm === 'mobile') {
    if (provider && provider !== 'cash' && provider !== 'espèces' && provider !== 'especes') {
      return provider;
    }
    return 'electronic';
  }

  // Provider stored directly in paymentMethod (e.g. bankily/sedad)
  if (provider && provider !== 'cash' && provider !== 'espèces' && provider !== 'especes') {
    // Prefer explicit paymentMethod when it is already a concrete provider id
    if (pm && pm !== 'electronic' && pm !== 'électronique') return pm;
    return provider;
  }

  return pm || 'cash';
}

export function isCashSale(sale) {
  return normalizePaymentMethodForSession(sale) === 'cash';
}

/** Entfernt doppelte Verkäufe (z. B. Checkout + Kassen-Update). */
export function dedupeSalesById(sales) {
  if (!Array.isArray(sales)) return [];
  const seen = new Set();
  const out = [];
  for (const sale of sales) {
    if (!sale || typeof sale !== 'object') continue;
    const key = String(
      sale.id || sale.orderId || `${sale.date || ''}_${normalizeSaleTotal(sale)}`
    );
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(sale);
  }
  return out;
}

/** Somme des ventes en espèces pour le calcul d'écart (Register-Income SSOT). */
export function sumCashSales(sales) {
  return dedupeSalesById(sales).reduce((total, sale) => {
    const { amount, channel } = resolveSaleRegisterIncome(sale);
    if (channel !== 'cash' || !(amount > 0)) return total;
    return total + amount;
  }, 0);
}

/** Somme des ventes électroniques (tous providers confondus). */
export function sumElectronicSales(sales) {
  return dedupeSalesById(sales).reduce((total, sale) => {
    const { amount, channel } = resolveSaleRegisterIncome(sale);
    if (channel !== 'electronic' || !(amount > 0)) return total;
    return total + amount;
  }, 0);
}

/** Ventile les ventes électroniques par provider (Bankily/Sedad/etc.). */
export function sumElectronicSalesByProvider(sales) {
  return dedupeSalesById(sales).reduce((acc, sale) => {
    const { amount, channel } = resolveSaleRegisterIncome(sale);
    if (channel !== 'electronic' || !(amount > 0)) return acc;
    const provider = String(
      sale.electronicPaymentMethod || sale.paymentMethod || 'electronic'
    ).toLowerCase();
    acc[provider] = (acc[provider] || 0) + amount;
    return acc;
  }, {});
}
